import { useState } from 'react';
import { ArrowLeft, FileText, User, Calendar } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SignatureCapture } from '@/components/SignatureCapture';
import { useToast } from '@/hooks/use-toast';

const SignatureRequest = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [signature, setSignature] = useState<string>('');
  const [formData, setFormData] = useState({
    documentName: '',
    documentType: 'compliance_report',
    signerName: '',
    signerRole: '',
    legalStatement: 'I certify that the information provided is accurate and complete to the best of my knowledge.',
    notes: ''
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSignature = (signatureData: string) => {
    setSignature(signatureData);
  };

  const handleSubmit = async () => {
    if (!signature) {
      toast({
        title: "Signature Required",
        description: "Please provide your digital signature before submitting.",
        variant: "destructive"
      });
      return;
    }

    if (!formData.documentName || !formData.signerName || !formData.signerRole) {
      toast({
        title: "Missing Information", 
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast({
        title: "Document Signed Successfully",
        description: `${formData.documentName} has been digitally signed and saved.`
      });
      
      navigate('/signatures');
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save signature. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="container mx-auto max-w-4xl">
        <div className="mb-8">
          <Link to="/signatures" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4">
            <ArrowLeft className="h-4 w-4" />
            Back to Signatures
          </Link>
          <div>
            <h1 className="heading-section mb-2">New Signature Request</h1>
            <p className="text-muted-foreground">Create and sign a new compliance document</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Document Information */}
          <Card className="glass-panel border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Document Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="documentName">Document Name *</Label>
                <Input
                  id="documentName"
                  value={formData.documentName}
                  onChange={(e) => handleInputChange('documentName', e.target.value)}
                  placeholder="e.g., Monthly Compliance Report - January 2024"
                  className="glass-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="documentType">Document Type *</Label>
                <Select value={formData.documentType} onValueChange={(value) => handleInputChange('documentType', value)}>
                  <SelectTrigger className="glass-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="glass-panel border-0 bg-background/95 backdrop-blur-sm z-50">
                    <SelectItem value="compliance_report">Compliance Report</SelectItem>
                    <SelectItem value="checklist">Checklist</SelectItem>
                    <SelectItem value="temperature_log">Temperature Log</SelectItem>
                    <SelectItem value="incident_report">Incident Report</SelectItem>
                    <SelectItem value="corrective_action">Corrective Action</SelectItem>
                    <SelectItem value="training_record">Training Record</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Additional Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Any additional information or context..."
                  className="glass-input min-h-[100px]"
                />
              </div>
            </CardContent>
          </Card>

          {/* Signer Information */}
          <Card className="glass-panel border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Signer Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="signerName">Full Name *</Label>
                <Input
                  id="signerName"
                  value={formData.signerName}
                  onChange={(e) => handleInputChange('signerName', e.target.value)}
                  placeholder="Enter your full name"
                  className="glass-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="signerRole">Role/Position *</Label>
                <Select value={formData.signerRole} onValueChange={(value) => handleInputChange('signerRole', value)}>
                  <SelectTrigger className="glass-input">
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent className="glass-panel border-0 bg-background/95 backdrop-blur-sm z-50">
                    <SelectItem value="Site Manager">Site Manager</SelectItem>
                    <SelectItem value="Kitchen Manager">Kitchen Manager</SelectItem>
                    <SelectItem value="Assistant Manager">Assistant Manager</SelectItem>
                    <SelectItem value="Kitchen Supervisor">Kitchen Supervisor</SelectItem>
                    <SelectItem value="Kitchen Staff">Kitchen Staff</SelectItem>
                    <SelectItem value="Food Safety Officer">Food Safety Officer</SelectItem>
                    <SelectItem value="Owner">Owner</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="p-4 bg-muted/20 rounded-lg">
                <div className="flex items-start gap-3 mb-3">
                  <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Signature Date</p>
                    <p className="text-sm text-muted-foreground">{new Date().toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  <strong>Legal Statement:</strong> {formData.legalStatement}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Signature Capture */}
        <Card className="glass-panel border-0 mt-8">
          <CardHeader>
            <CardTitle>Digital Signature</CardTitle>
            <p className="text-sm text-muted-foreground">
              Please sign below to acknowledge and certify this document
            </p>
          </CardHeader>
          <CardContent>
            <SignatureCapture 
              onSignature={handleSignature}
              signature={signature}
            />
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end gap-4 mt-8">
          <Button variant="outline" className="glass-button" onClick={() => navigate('/signatures')}>
            Cancel
          </Button>
          <Button className="cta-primary" onClick={handleSubmit}>
            Sign & Submit Document
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SignatureRequest;